import { get } from 'vuex-pathify';

export default {
  computed: {
    metadata: get('layers/metadata'),
    currentLayerDataSource: get('layers/currentLayer@data_source_name'),
    currentLayerFormSchema: get('layers/currentLayer@form_schema.elements'),
    attributesMetadata: get('layers/metadata@:currentLayerDataSource.attributes_schema.attributes'),
  },
  methods: {
    getForm(elements, metadata, filteredOutAttributes = [], relationalValues = this.relationValuesMappingAll) {
      return elements
        .filter(
          element =>
            (element.element_type === 'group' && element.elements.length > 0) ||
            (element.element_type === 'attribute_holder' && !filteredOutAttributes.includes(element.attribute))
        )
        .map(element => {
          if (element.element_type === 'group') {
            return {
              ...element,
              elements: this.getForm(element.elements, metadata, filteredOutAttributes, relationalValues),
            };
          }
          const {
            type,
            data_type: dataType,
            measurement_unit,
            dict_colors,
            allowed_values,
            allowed_relations,
            read_only: readonly,
            relation,
            nullable,
          } = this.getAttribute(element.attribute, metadata);
          const isSelectInput = type === 'dict' || type === 'variable_relation_pointer' || type === 'relation';
          const hasColors =
            isSelectInput &&
            dict_colors &&
            dict_colors.length === (allowed_relations || allowed_values).length &&
            dict_colors.every(color => Boolean(color));
          return {
            ...element,
            editable: !readonly,
            unit: this.getSymbol(measurement_unit),
            colors: hasColors ? dict_colors : undefined,
            items: isSelectInput
              ? type === 'relation'
                ? this.getRelationalDict(
                    relationalValues,
                    relation.attribute,
                    relation.data_source,
                    relation.representation
                  )
                : this.getDictItems(type, allowed_relations || allowed_values)
              : undefined,
            dataType: this.getDataType(isSelectInput, dataType, hasColors),
            icon: dataType.name === 'datetime' ? 'mdi-calendar' : '',
            rules: this.getRules({ required: element.required, maxLength: dataType.max_length, readonly }),
            nullable,
          };
        });
    },
    getRelationalDict(relationalValues, attribute, datasource, representation) {
      return this.$store.get('attributesSchema/relationValuesMapping')[datasource]?.[attribute]?.[representation] || [];
    },
    getAttribute(attribute, metadata = this.attributesMetadata) {
      return metadata.find(attr => attr.name === attribute);
    },
    getSymbol(unit) {
      return unit ? unit.symbol : null;
    },
    getRules({ readonly, required, maxLength }) {
      const rules = [];
      if (readonly) {
        return rules;
      }
      if (required) {
        rules.push(v => !!v || v === false || v === 0 || 'rules.required');
      }
      if (maxLength) {
        rules.push(v => v.length > maxLength || 'rules.maxLengthExceeded');
      }
      return rules;
    },
    getDataType(isSelectInput, dataType, hasColors) {
      return isSelectInput
        ? {
            ...dataType,
            name: hasColors ? 'selectcolor' : 'select',
          }
        : dataType;
    },
    getDictItems(type, items) {
      if (type === 'variable_relation_pointer') {
        return items.map(({ data_source, data_source_verbose_name }) => ({
          value: data_source,
          text: data_source_verbose_name,
        }));
      }
      return items;
    },
    getValues(elements, valuesObject) {
      return elements.reduce((total, element) => {
        if (element.element_type === 'group') {
          return { ...total, ...this.getValues(element.elements, valuesObject) };
        }
        return {
          ...total,
          [element.attribute]: this.getValue(element.attribute, element.default_value_policy, valuesObject),
        };
      }, {});
    },
    getValue(attribute, defaultValuePolicy, valuesObject) {
      if (this.isNew && defaultValuePolicy) {
        return this.getDefaultValueHandler(defaultValuePolicy);
      }
      let value = null;
      if (valuesObject?.[attribute] || valuesObject?.[attribute] === false || valuesObject?.[attribute] === 0) {
        value = valuesObject[attribute];
      } else if (
        this.properties?.[attribute] ||
        this.properties?.[attribute] === false ||
        valuesObject?.[attribute] === 0
      ) {
        value = this.properties[attribute];
      } else if (this.feature?.[attribute] || this.feature?.[attribute] === false || valuesObject?.[attribute] === 0) {
        value = this.feature[attribute];
      }
      return value;
    },
    getFlattenProperties(values, idPropertyName = this.idPropertyName) {
      return {
        ...values.properties,
        // in some cases id property has different name than id but its value is available under id key in fetched feature
        [idPropertyName]: this.getIdValue(values, idPropertyName),
      };
    },
    getIdValue(values, idPropertyName) {
      return this.$_hasAnyValue(values.id) ? values.id : values.properties.id || values.properties[idPropertyName];
    },
    getAttributeTextValue(values, attribute) {
      let value = values[attribute.attribute];
      if (
        attribute.items &&
        attribute.items.every(item => {
          return typeof item === 'object' && Object.keys(item).includes('text') && Object.keys(item).includes('value');
        })
      ) {
        return attribute.items.find(item => item.value == value)?.text
          ? attribute.items.find(item => item.value == value)?.text
          : '';
      }
      const dataTypeName = attribute.dataType.name;
      if (dataTypeName === 'datetime') {
        return value ? this.$dayjs(value).format('L, LT') : '';
      } else if (dataTypeName === 'date') {
        return value ? this.$dayjs(value).format('L') : '';
      } else if (dataTypeName === 'boolean') {
        const { true: trueValue, false: falseValue } = attribute.dataType;
        const trueText = trueValue || trueValue === 0 ? trueValue : this.$i18n.t('inputs.true');
        const falseText = falseValue || falseValue === 0 ? falseValue : this.$i18n.t('inputs.false');
        return value ? trueText : falseText;
      }
      return value === null ? '' : value;
    },
  },
};
